import { createStyles } from 'antd-style';

/**
 * 顶栏两个下拉（账号、语言）的外观。
 *
 * 和登录/授权页同一套语言：12px 圆角的面板、8px 圆角的条目、
 * 偏蓝的灰做次要文字。写成覆盖 antd 的形式而不是自己撸一个 Popover，
 * 是为了保留 antd Dropdown 的键盘导航和无障碍语义。
 */
export const useMenuStyles = createStyles(({ css, token }) => ({
  panel: css`
    .ant-dropdown-menu {
      min-width: 232px;
      /* 横向内距一律用 antd 的默认值，不另外加一层 ——
         多加就会和其他下拉对不齐，而且面板和条目两层内距会叠起来。 */
      border-radius: 12px;
      border: 1px solid ${token.colorBorderSecondary};
      box-shadow:
        0 1px 2px 0 rgba(0, 0, 0, 0.03),
        0 6px 16px -6px rgba(0, 0, 0, 0.12),
        0 2px 4px 0 rgba(0, 0, 0, 0.02);
    }

    .ant-dropdown-menu-item,
    .ant-dropdown-menu-item-group-title {
      border-radius: 8px;
    }

    .ant-dropdown-menu-item {
      font-size: 14px;
      line-height: 1.4;
    }

    .ant-dropdown-menu-item-group-title {
      font-size: 12px;
      color: ${token.colorTextTertiary};
    }

    /* antd 给分组列表加了 margin: 0 8px，于是「已登录账号」那组的条目
       比下面几条左右各缩进 8px，高亮块宽度对不上。这里用 && 提权压过它 ——
       单个类选择器权重和 antd 的一样，会被源码顺序反超。 */
    && .ant-dropdown-menu-item-group-list {
      margin-inline: 0;
    }

    /* 账号条目单独放宽：两行文字的条目挨太紧会糊成一片 */
    .ant-dropdown-menu-item-group-list .ant-dropdown-menu-item {
      padding-block: 8px;
      margin-block: 2px;
    }

    .ant-dropdown-menu-item .anticon,
    .ant-dropdown-menu-item svg {
      color: ${token.colorTextSecondary};
    }

    .ant-dropdown-menu-item-divider {
      margin: 6px 4px;
      background: ${token.colorBorderSecondary};
    }
  `,

  /* 账号条目：头像 + 姓名 + 邮箱，当前账号右侧打勾 */
  account: css`
    display: flex;
    align-items: center;
    gap: 11px;
    width: 100%;
  `,

  avatar: css`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    flex: none;
    color: #fff;
    font-weight: 600;
    font-size: 13px;
  `,

  /* 姓名和邮箱上下两行，行距压紧 —— 两行之间留白一大就散了 */
  accountText: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
  `,

  name: css`
    font-size: 14px;
    line-height: 1.25;
    color: ${token.colorText};
  `,

  mail: css`
    font-size: 11.5px;
    line-height: 1.25;
    color: ${token.colorTextSecondary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,

  tick: css`
    margin-left: auto;
    flex: none;
  `,

  /* ── 手机端的底部弹出面板 ── */
  sheetBody: css`
    padding: 8px 0 max(12px, env(safe-area-inset-bottom));
  `,

  sheetGroup: css`
    padding: 6px 20px 8px;
    font-size: 12px;
    color: ${token.colorTextTertiary};
  `,

  sheetRow: css`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 20px;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 15px;
    color: ${token.colorText};
    cursor: pointer;
    /* 手拿着点，触点要够大 */
    min-height: 52px;

    &:active {
      background: ${token.colorFillQuaternary};
    }

    svg {
      color: ${token.colorTextSecondary};
      flex: none;
    }
  `,

  sheetDivider: css`
    height: 1px;
    margin: 6px 0;
    background: ${token.colorBorderSecondary};
  `,

  /* 语言切换：顶栏上那个圆角方形按钮 */
  langTrigger: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${token.colorTextSecondary};
    cursor: pointer;
    transition: background 0.14s, color 0.14s;

    &:hover {
      background: ${token.colorFillQuaternary};
      color: ${token.colorText};
    }

    &:focus-visible {
      outline: 2px solid ${token.colorPrimary};
      outline-offset: 2px;
    }
  `,

  langItem: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,

  langTick: css`
    margin-left: auto;
    flex: none;
    color: ${token.colorPrimary};
  `,
}));
